if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    static mediaQuery = {
      mobile: '(max-width: 991px)'
    };

    constructor() {
      super();

      this.productElJSON = null;
      this.currentProductObjectJSON = null;
      this.currentCartObjectJSON = null;
      this.currentProductUrl = null;
      this.currentProductSellingPlansJSON = [];
      this.form = this.querySelector('form');
      this.formType = this.form.getAttribute('data-type');
      this.form.querySelector('[name=id]').disabled = false;
      this.initProduct();
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      this.subscriptionWidgetId = this.dataset.subscriptionWidgetId;
      this.isSubscriptionSplit = this.dataset.subscriptionSplit !== undefined;
      this.isMainProductForm = this.dataset.mainProductForm !== undefined;
      this.mainProductSection = this.isMainProductForm ? this.closest('.js-main-product-section') : null;
      this.isSubscriptionWithTripod = this.form.querySelector('[name=subscription_tripod_product_id]');

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    initProduct() {
      const productElJSON = this.querySelector(".js-data-product-object");

      if (!productElJSON) return null;

      this.productElJSON = productElJSON;

      try {
        const {product, cart, product_url, selling_plans} = JSON.parse(productElJSON.innerText);
        this.currentProductObjectJSON = product;
        this.currentCartObjectJSON = cart;
        this.currentProductUrl = product_url;
        this.currentProductSellingPlansJSON = Array.isArray(selling_plans) ? selling_plans : [];
      } catch (error) {
        console.error(`Product::ERROR::${error.message}`);
      }
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();

      if (this.subscriptionWidgetId && SubscriptionServiceManager) {
        const isButtonDisabled = this.submitButton.classList.contains('disabled');

        if(isButtonDisabled || this.openModal()) {
          return;
        }

        await this.addToCartSubscriptionProcess();
      } else {
        await this.addToCartProcess();
      }
    }

    openModal() {
      const isMobile = matchMedia(ProductForm.mediaQuery.mobile).matches;
      const modalSelector = this.submitButton.getAttribute('data-modal');
      const modal = isMobile && modalSelector ? document.querySelector(modalSelector) : null;
      if (modal) {
        modal.show(this.submitButton);
        return true;
      }
      return false;
    }

    async addToCartSubscriptionProcess() {
      this.subscriptionServiceManager = new SubscriptionServiceManager();

      const options = {
        widgetId: this.subscriptionWidgetId,
        targetButton: this.submitButton
      };

      if (!this.isSubscriptionSplit) {
        options.form = this.form;
      }

      await this.subscriptionServiceManager.addToCart(options);
    }

    hasSubscriptionInCart(cart) {
      return cart.items.some(item => {
        const isSubscriptionProduct = item.product_type === window.cartStrings.subscriptionProductType;
        const isSellingPlanAllocation = item.selling_plan_allocation;
        const [propertyKey, propertyValue] = Object.entries(SubscriptionServiceManager.subscriptionPrepaidPropertyMain)[0];
        const isPrepaidPackage = item.properties && item.properties[propertyKey] === propertyValue;
        return isSubscriptionProduct && isSellingPlanAllocation && !isPrepaidPackage;
      });
    }

    getUltimateEditionWidget() {
      const widgetInSection = this.mainProductSection?.querySelector('[data-ultimate-edition]');
      if (widgetInSection) return widgetInSection;

      const sectionId = this.dataset.section || this.getAttribute('data-section');
      if (sectionId) {
        const widgetBySection = document.querySelector(`[data-ultimate-edition][data-section-id="${sectionId}"]`);
        if (widgetBySection) return widgetBySection;
      }

      return document.querySelector('[data-ultimate-edition]');
    }

    getSelectedUltimateOption(widget = null) {
      const resolvedWidget = widget || this.getUltimateEditionWidget();
      if (!resolvedWidget) return null;

      const currentVariantId = this.form?.querySelector('[name="id"]')?.value;
      const optionByVariant = currentVariantId
        ? resolvedWidget.querySelector(`[data-ultimate-edition-option][data-variant-id="${currentVariantId}"]`)
        : null;

      return optionByVariant
        || resolvedWidget.querySelector('[data-ultimate-edition-option][aria-pressed="true"]')
        || resolvedWidget.querySelector('[data-ultimate-edition-option].is-active');
    }


    getSelectedUltimateGiftVariantIds(widget = null) {
      const resolvedWidget = widget || this.getUltimateEditionWidget();
      if (!resolvedWidget) return [];

      const selectedOption = this.getSelectedUltimateOption(resolvedWidget);
      const selectedVariantId = selectedOption?.dataset?.variantId;
      const ultimateVariantId = resolvedWidget.dataset.ultimateVariantId;

      if (!selectedVariantId || !ultimateVariantId || String(selectedVariantId) !== String(ultimateVariantId)) {
        return [];
      }

      const rawGiftVariantIds = selectedOption?.dataset?.giftVariantIds;

      if (!rawGiftVariantIds) return [];

      return [...new Set(
        rawGiftVariantIds
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      )];
    }

    getSelectedUltimateGiftLessonsMap(widget = null) {
      const resolvedWidget = widget || this.getUltimateEditionWidget();
      if (!resolvedWidget) return {};

      const selectedOption = this.getSelectedUltimateOption(resolvedWidget);
      if (!selectedOption) {
        return {};
      }

      const rawMap = selectedOption.dataset?.giftLessonsMap || '';
      if (!rawMap) return {};

      return rawMap.split(',').reduce((acc, pair) => {
        const [variantIdRaw, countRaw] = pair.split(':');
        const variantId = (variantIdRaw || '').trim();
        const count = (countRaw || '').trim();
        if (variantId && count) acc[variantId] = count;
        return acc;
      }, {});
    }

    getStandardGiftDataFromWidget() {
      const widget = this.getUltimateEditionWidget();
      if (!widget) return null;

      const standardVariantId = widget.dataset.standardVariantId;
      const standardOption = standardVariantId
        ? widget.querySelector(`[data-ultimate-edition-option][data-variant-id="${standardVariantId}"]`)
        : widget.querySelector('.ultimate-edition-widget__card--standard[data-ultimate-edition-option]');

      const giftVariantId = standardOption?.dataset?.giftVariantIds
        ?.split(',')
        .map((id) => id.trim())
        .find(Boolean);

      if (!giftVariantId) return null;

      return {
        variantId: giftVariantId,
        productType: standardOption?.dataset?.giftProductType || '',
        lessonsCount: standardOption?.dataset?.giftLessonsCount || '',
        title: standardOption?.dataset?.giftTitle || '',
        image: standardOption?.dataset?.giftImage || '',
        subtitle: standardOption?.dataset?.giftSubtitle || '',
      };
    }

    getFathersDayGiftVariantId() {
      const toggle = document.querySelector('.fathers-day-offer__toggle-input:checked');
      return toggle?.dataset.giftVariantId || null;
    }

    getFathersDayLessonVariantId() {
      const input = document.querySelector('.js-fathers-day-lesson-input');
      return input?.dataset.variantId || null;
    }

    getFathersDayAllGiftVariantId() {
      return document.querySelector('.fathers-day-offer__toggle-input')?.dataset.giftVariantId || null;
    }

    getGiftProductInput() {
      const fromMainSection = this.mainProductSection?.querySelector('#GiftProduct');
      if (fromMainSection) return fromMainSection;

      let sectionId = this.dataset.section || this.getAttribute('data-section');
      if (!sectionId) {
        const rawFormId = this.form?.getAttribute?.('id') ?? this.form?.id ?? '';
        const formId = typeof rawFormId === 'string' ? rawFormId : String(rawFormId || '');
        const prefix = 'product-form-';
        if (formId.startsWith(prefix)) {
          sectionId = formId.slice(prefix.length);
        }
      }
      if (sectionId) {
        const fromSectionById = document.querySelector(`#shopify-section-${sectionId} #GiftProduct`);
        if (fromSectionById) return fromSectionById;
      }

      const localSection = this.closest('[id^="shopify-section-"]');
      const fromLocalSection = localSection?.querySelector('#GiftProduct');
      if (fromLocalSection) return fromLocalSection;

      const allGiftInputs = Array.from(document.querySelectorAll('#GiftProduct'));
      if (!allGiftInputs.length) {
        return this.form.querySelector('input[name="gwp"]');
      }

      const withVariantAndTitle = allGiftInputs.find((input) => input.dataset.variantId && input.dataset.title);
      if (withVariantAndTitle) return withVariantAndTitle;

      const withVariant = allGiftInputs.find((input) => input.dataset.variantId);
      if (withVariant) return withVariant;

      return allGiftInputs[0];
    }

    getStandardGiftData() {
      const widgetData = this.getStandardGiftDataFromWidget() || {};
      let gwpInput = this.getGiftProductInput();

      if ((!gwpInput || !gwpInput.dataset?.variantId) && widgetData.variantId) {
        gwpInput = document.querySelector(`input[name="gwp"][data-variant-id="${widgetData.variantId}"]`);
      }
      if (!gwpInput || !gwpInput.dataset?.variantId) {
        gwpInput = document.querySelector('input[name="gwp"][data-variant-id]');
      }

      const inputData = gwpInput?.dataset?.variantId
          ? {
              variantId: gwpInput.dataset.variantId,
              productType: gwpInput.dataset.productType || '',
              lessonsCount: gwpInput.dataset.lessonsCount || '',
              title: gwpInput.dataset.title || '',
              image: gwpInput.dataset.image || '',
              subtitle: gwpInput.dataset.subtitle || '',
              displayTitle: gwpInput.dataset.displayTitle || '',
              forceSingleQuantity: gwpInput.dataset.forceSingleQuantity === 'true',
            }
          : {};
      const cacheData = window._gwpCache || {};

      return {
        variantId: widgetData.variantId || inputData.variantId || '',
        productType: widgetData.productType || inputData.productType || '',
        lessonsCount: widgetData.lessonsCount || inputData.lessonsCount || '',
        title: widgetData.title || inputData.title || cacheData.title || '',
        image: widgetData.image || inputData.image || cacheData.image || '',
        subtitle: widgetData.subtitle || inputData.subtitle || cacheData.subtitle || '',
        displayTitle: inputData.displayTitle || '',
        forceSingleQuantity: Boolean(inputData.forceSingleQuantity),
      };
    }

    getUltimateEditionData(widget = null) {
      const resolvedWidget = widget || this.getUltimateEditionWidget();

      if (!resolvedWidget) {
        return {
          isUltimateSelected: false,
          isStandardSelected: false,
          title: '',
          hasMultipleGifts: false,
          standardUpgradeToVariantId: '',
          standardUpgradeToTitle: '',
          standardUpgradeToImage: ''
        };
      }

      const selectedOption = this.getSelectedUltimateOption(resolvedWidget);
      const selectedVariantId = selectedOption?.dataset?.variantId;
      const ultimateVariantId = resolvedWidget.dataset.ultimateVariantId;
      const standardVariantId = resolvedWidget.dataset.standardVariantId;
      const title = selectedOption?.querySelector('.ultimate-edition-widget__title')?.textContent?.trim() || '';

      return {
        isUltimateSelected: Boolean(
          selectedVariantId
          && ultimateVariantId
          && String(selectedVariantId) === String(ultimateVariantId)
        ),
        isStandardSelected: Boolean(
          selectedVariantId
          && standardVariantId
          && String(selectedVariantId) === String(standardVariantId)
        ),
        title: title,
        hasMultipleGifts: selectedOption?.dataset?.hasMultipleGifts === 'true',
        standardUpgradeToVariantId: resolvedWidget.dataset.standardUpgradeToVariantId || '',
        standardUpgradeToTitle: resolvedWidget.dataset.standardUpgradeToTitle || '',
        standardUpgradeToImage: resolvedWidget.dataset.standardUpgradeToImage || ''
      };
    }

    getCurrentVariantData(preferredVariantId = null) {
      const variantId = preferredVariantId || this.form?.querySelector('[name="id"]')?.value;
      if (!variantId) return null;

      const variantDataScript =
        this.mainProductSection?.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]')
        || this.closest('[id^="shopify-section-"]')?.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]')
        || document.querySelector(`variant-radios[data-section="${this.dataset.section}"] script[type="application/json"]`)
        || document.querySelector(`variant-selects[data-section="${this.dataset.section}"] script[type="application/json"]`);

      if (!variantDataScript) return null;

      try {
        const variants = JSON.parse(variantDataScript.textContent);
        return variants.find((variant) => String(variant.id) === String(variantId)) || null;
      } catch (error) {
        return null;
      }
    }

    appendUltimateEditionProperties(formData, itemIndex, ultimateEditionData) {
      if (ultimateEditionData.isStandardSelected) {
        formData.append(`items[${itemIndex}][properties][_is-standard-edition]`, 'true');
      }

      if (ultimateEditionData.isStandardSelected && ultimateEditionData.standardUpgradeToVariantId) {
        formData.append(`items[${itemIndex}][properties][_ultimate-upgrade-to-variant-id]`, ultimateEditionData.standardUpgradeToVariantId);
        if (ultimateEditionData.standardUpgradeToTitle) {
          formData.append(`items[${itemIndex}][properties][_ultimate-upgrade-title]`, ultimateEditionData.standardUpgradeToTitle);
        }
        if (ultimateEditionData.standardUpgradeToImage) {
          formData.append(`items[${itemIndex}][properties][_ultimate-upgrade-image]`, ultimateEditionData.standardUpgradeToImage);
        }
      }

      if (ultimateEditionData.isUltimateSelected) {
        formData.append(`items[${itemIndex}][properties][_is-ultimate-edition]`, 'true');
        if (ultimateEditionData.title) {
          formData.append(`items[${itemIndex}][properties][_ultimate-edition-title]`, ultimateEditionData.title);
        }
      }

    }

    async getCartState() {
      const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    }

    getNonPrepaidSubscriptionCountFromFormData(formData) {
      const [propertyKey, propertyValue] =
          Object.entries(SubscriptionServiceManager.subscriptionPrepaidPropertyMain)[0];

      const sellingPlans = new Map();
      const prepaidFlags = new Map();

      for (const [key, value] of formData.entries()) {
        const sellingPlanMatch = key.match(/^items\[(\d+)\]\[selling_plan\]$/);

        if (sellingPlanMatch) {
          sellingPlans.set(sellingPlanMatch[1], value);
          continue;
        }

        const propMatch = key.match(/^items\[(\d+)\]\[properties\]\[(.+?)\]$/);

        if (propMatch) {
          const index = propMatch[1];
          const propName = propMatch[2];

          if (propName === propertyKey && value === propertyValue) {
            prepaidFlags.set(index, true);
          }
        }
      }

      let count = 0;

      for (const [index] of sellingPlans.entries()) {
        const isPrepaid = prepaidFlags.get(index) === true;

        if (!isPrepaid) count += 1;
      }

      return count;
    }

    showNotificationModal(message) {
      window.dispatchEvent(new CustomEvent("notification-modal:show", {
        detail: {
          message: message
        }
      }));
    }

    async addToCartProcess()  {
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const earlyFormData = new FormData(this.form);
      const earlySP = earlyFormData.get('selling_plan');
      // window._cartState is seeded by initCartCache and refreshed by updateCartAndProductJSON
      // after every mutation, so this is normally a synchronous read and the optimistic drawer
      // opens on the click frame. Awaiting a fresh /cart.js here cost a full round trip before
      // any UI ran. The fetch stays as the fallback for the window before the cache is seeded.
      const cartState = window._cartState || await this.getCartState();
      if (earlySP && this.hasSubscriptionInCart(cartState)) {
        this.showNotificationModal(window?.cartStrings?.alreadyIncludedSubscriptionError);
        this.submitButton.setAttribute('aria-disabled', false);
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        return;
      }

      const isAdditionalProductForm = !!this.form.closest('.additional-product__item');

      if (isAdditionalProductForm) {
        const addonConfig = fetchConfig('javascript');
        addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete addonConfig.headers['Content-Type'];
        const sectionIds = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        const addonGwpInput = this.form.querySelector('[name="gwp"]');
        if (addonGwpInput?.dataset?.variantId) {
          const mainVariantId = earlyFormData.get('id');
          const mainQuantity = parseInt(earlyFormData.get('quantity') || '1', 10) || 1;
          const gwpVariantId = addonGwpInput.dataset.variantId;
          const gwpProductType = String(addonGwpInput.dataset.productType || '').trim().toLowerCase();
          const gwpLessonsCount = String(addonGwpInput.dataset.lessonsCount || '').trim();
          const displayTitle = addonGwpInput.dataset.displayTitle || '';
          const forceSingleQuantity = addonGwpInput.dataset.forceSingleQuantity === 'true';
          const mainProperties = {};
          const gwpProperties = {
            '_gwp-product': 'true',
            '_gwp-product-for': String(mainVariantId),
          };

          for (const [key, value] of earlyFormData.entries()) {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch) mainProperties[propertyMatch[1]] = value;
          }

          mainProperties['_gwp-product-related'] = String(gwpVariantId);

          if (displayTitle) {
            gwpProperties['_gwp-display-title'] = displayTitle;
          }
          if (forceSingleQuantity) {
            gwpProperties['_gwp-force-single-quantity'] = 'true';
          }
          if (gwpProductType === 'lesson' || gwpLessonsCount) {
            gwpProperties['_lessons'] = 'true';
            if (gwpLessonsCount) gwpProperties['_lessons_count'] = gwpLessonsCount;
          }

          const mainItem = {
            id: parseInt(mainVariantId, 10),
            quantity: mainQuantity,
            properties: mainProperties,
          };
          const sellingPlanValue = earlyFormData.get('selling_plan');
          if (sellingPlanValue) mainItem.selling_plan = parseInt(sellingPlanValue, 10);

          addonConfig.body = JSON.stringify({
            items: [
              mainItem,
              {
                id: parseInt(gwpVariantId, 10),
                quantity: forceSingleQuantity ? 1 : mainQuantity,
                properties: gwpProperties,
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          });
          addonConfig.headers['Content-Type'] = 'application/json';
        } else {
          if (this.cart) {
            earlyFormData.append('sections', sectionIds);
            earlyFormData.append('sections_url', window.location.pathname);
          }
          addonConfig.body = earlyFormData;
        }

        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          try { this.showOptimisticCartUpdate(earlyFormData); } catch (e) { console.error('Optimistic update failed:', e); }
        }

        window._CART_REQUESTS = window._CART_REQUESTS || 0;
        window._CART_REQUESTS++;

        fetch(`${routes.cart_add_url}.js`, addonConfig)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.removeSkeletonItem();
              revertOptimisticCartItemQuantity();
              if (window.CartOptimist) CartOptimist.revert();
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            window._CART_REQUESTS--;

            this.error = false;
            this.cart.renderContents(response, 'add-to-cart-form');
            document.dispatchEvent(new CustomEvent("init:upsell-slider"));
            document.dispatchEvent(new CustomEvent("cart:reorder-addons"));

            updateCartAndProductJSON();
          })
          .catch((e) => {
            window._CART_REQUESTS--;
            this.removeSkeletonItem();
            revertOptimisticCartItemQuantity();
            if (window.CartOptimist) CartOptimist.revert();
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });

        return;
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      const upsellInputs = this.mainProductSection ? [...this.mainProductSection.querySelectorAll('.js-upsell-product-input:checked')] : [];
      const bundleProductInput = this.mainProductSection && this.mainProductSection.querySelector('.js-bundle-product-input:checked');
      const gwpInput = this.getGiftProductInput();
      const igItemProperty = window.igCustomData?.itemProperty;
      const isSubscriptionInCart = this.hasSubscriptionInCart(cartState);

      const mainVariantId = formData.get('id');
      const mainQuantity = formData.get('quantity') || 1;
      const mainSellingPlan = formData.get('selling_plan');
      let mainIndex = 0;
      let plainEntries = Array.from(formData.entries());

      if (mainSellingPlan && isSubscriptionInCart) {
        this.removeSkeletonItem();
        revertOptimisticCartItemQuantity();
        if (window.CartOptimist) CartOptimist.revert();
        if (this.cart && typeof this.cart.close === 'function') this.cart.close();
        this.showNotificationModal(window?.cartStrings?.alreadyIncludedSubscriptionError);
        this.submitButton.setAttribute('aria-disabled', false);
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        return;
      }

      formData.delete('id');
      formData.delete('quantity');
      formData.delete('selling_plan');

      if (this.isSubscriptionWithTripod) {
        formData.append(`items[0][id]`, this.isSubscriptionWithTripod.value);
        formData.append(`items[0][quantity]`, mainQuantity);
        formData.append(`items[0][properties][_tripod_subscription_type]`, "tripod");
        mainIndex++;
      }

      const baseVariantId = bundleProductInput ? bundleProductInput.dataset.variantId : mainVariantId;
      const ultimateEditionWidget = this.getUltimateEditionWidget();
      const selectedUltimateGiftVariantIds = this.getSelectedUltimateGiftVariantIds(ultimateEditionWidget);
      const selectedUltimateGiftLessonsMap = this.getSelectedUltimateGiftLessonsMap(ultimateEditionWidget);
      const ultimateEditionData = this.getUltimateEditionData(ultimateEditionWidget);
      const standardGiftData = (!selectedUltimateGiftVariantIds.length && ultimateEditionData.isStandardSelected)
        ? this.getStandardGiftData()
        : null;
      const standardGiftDataForLessons = ultimateEditionData.isStandardSelected
        ? this.getStandardGiftData()
        : null;
      const shouldUseStandardGiftFromMetafield = ultimateEditionData.isStandardSelected;
      const shouldAddSingleGwp = !selectedUltimateGiftVariantIds.length
            && (shouldUseStandardGiftFromMetafield ? standardGiftData?.variantId : gwpInput);

      if (selectedUltimateGiftVariantIds.length) {
          [...selectedUltimateGiftVariantIds].reverse().forEach((giftVariantId) => {
              const normalizedGiftVariantId = String(giftVariantId);
              let lessonsCount = selectedUltimateGiftLessonsMap[normalizedGiftVariantId] || '';
              const standardGiftType = String(standardGiftDataForLessons?.productType || '').trim().toLowerCase();
              const standardGiftCount = String(standardGiftDataForLessons?.lessonsCount || '').trim();
              const isStandardLessonGift = !!(
                ultimateEditionData.isStandardSelected
                && standardGiftDataForLessons
                && String(standardGiftDataForLessons.variantId || '') === normalizedGiftVariantId
                && (standardGiftType === 'lesson' || standardGiftCount !== '')
              );

              if (!String(lessonsCount).trim() && isStandardLessonGift && standardGiftCount !== '') {
                lessonsCount = standardGiftCount;
              }

              formData.append(`items[${mainIndex}][id]`, giftVariantId);
              formData.append(`items[${mainIndex}][quantity]`, mainQuantity);
              formData.append(`items[${mainIndex}][properties][_gwp-product]`, "true");
              formData.append(`items[${mainIndex}][properties][_gwp-product-for]`, baseVariantId);
              if (ultimateEditionData.hasMultipleGifts) {
                formData.append(`items[${mainIndex}][properties][_gwp-ultimate]`, "true");
              }
              if (String(lessonsCount).trim() !== '' || isStandardLessonGift) {
                formData.append(`items[${mainIndex}][properties][_lessons]`, "true");
              }
              if (String(lessonsCount).trim() !== '') {
                formData.append(`items[${mainIndex}][properties][_lessons_count]`, lessonsCount);
              }

              if (igItemProperty) {
                  const [key, value] = Object.entries(igItemProperty)[0];
                  formData.append(`items[${mainIndex}][properties][${key}]`, value);
              }

              mainIndex++;
          });
        } else if (shouldAddSingleGwp) {
        const variantId = shouldUseStandardGiftFromMetafield
          ? standardGiftData.variantId
          : (gwpInput ? gwpInput.dataset.variantId : standardGiftData.variantId);
        const productType = shouldUseStandardGiftFromMetafield
          ? (standardGiftData.productType || '')
          : (gwpInput ? gwpInput.dataset.productType : standardGiftData.productType);
        let lessonsCount = shouldUseStandardGiftFromMetafield
          ? (standardGiftData.lessonsCount || '')
          : (gwpInput ? gwpInput.dataset.lessonsCount : standardGiftData.lessonsCount);
        if (!String(lessonsCount || '').trim() && variantId && selectedUltimateGiftLessonsMap[String(variantId)]) {
          lessonsCount = selectedUltimateGiftLessonsMap[String(variantId)];
        }
        const normalizedProductType = String(productType || '').trim().toLowerCase();
        const hasLessonsCount = String(lessonsCount || '').trim() !== '';
        const displayTitle = shouldUseStandardGiftFromMetafield
          ? (standardGiftData.displayTitle || '')
          : (gwpInput ? (gwpInput.dataset.displayTitle || '') : '');
        const forceSingleQuantity = shouldUseStandardGiftFromMetafield
          ? Boolean(standardGiftData.forceSingleQuantity)
          : Boolean(gwpInput && gwpInput.dataset.forceSingleQuantity === 'true');
        const giftQuantity = forceSingleQuantity ? '1' : mainQuantity;

        formData.append(`items[${mainIndex}][id]`, variantId);
        formData.append(`items[${mainIndex}][quantity]`, giftQuantity);
        formData.append(`items[${mainIndex}][properties][_gwp-product]`, "true");
        formData.append(`items[${mainIndex}][properties][_gwp-product-for]`, baseVariantId);
        if (displayTitle) {
          formData.append(`items[${mainIndex}][properties][_gwp-display-title]`, displayTitle);
        }
        if (forceSingleQuantity) {
          formData.append(`items[${mainIndex}][properties][_gwp-force-single-quantity]`, "true");
        }
        if (ultimateEditionData.hasMultipleGifts) {
          formData.append(`items[${mainIndex}][properties][_gwp-ultimate]`, "true");
        }

        if (normalizedProductType === "lesson" || hasLessonsCount) {
          formData.append(`items[${mainIndex}][properties][_lessons]`, "true");
          if (hasLessonsCount) {
            formData.append(`items[${mainIndex}][properties][_lessons_count]`, lessonsCount);
          }
        }

        if (igItemProperty) {
          const [key, value] = Object.entries(igItemProperty)[0];
          formData.append(`items[${mainIndex}][properties][${key}]`, value);
        }

        mainIndex++;
      }

      if (bundleProductInput) {
        const variantId = bundleProductInput.dataset.variantId;
        const productType = bundleProductInput.dataset.productType;
        const preOrder = bundleProductInput.dataset.preOrder !== undefined;

        formData.append(`items[${mainIndex}][id]`, variantId);
        formData.append(`items[${mainIndex}][quantity]`, mainQuantity);

        if (productType === "Matt") {
          formData.append(`items[${mainIndex}][properties][_is_product_matt]`, "true");
        }

        if (preOrder) {
          formData.append(`items[${mainIndex}][properties][Stock status]`, "Pre-order");
        }
        this.appendUltimateEditionProperties(formData, mainIndex, ultimateEditionData);

        if (igItemProperty) {
          const [key, value] = Object.entries(igItemProperty)[0];
          formData.append(`items[${mainIndex}][properties][${key}]`, value);
        }

        if (gwpInput || (shouldUseStandardGiftFromMetafield && standardGiftData)) {
          const variantId = shouldUseStandardGiftFromMetafield
            ? standardGiftData?.variantId
            : (gwpInput ? gwpInput.dataset.variantId : null);
          if (variantId) {
            formData.append(`items[${mainIndex}][properties][_gwp-product-related]`, variantId);
          }
        }
      } else {
        formData.append(`items[${mainIndex}][id]`, mainVariantId);
        formData.append(`items[${mainIndex}][quantity]`, mainQuantity);

        plainEntries.forEach(([key, value]) => {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[(.+?)\]/)?.[1];
            if (propName) formData.append(`items[${mainIndex}][properties][${propName}]`, value);
          }
        });

        if (mainSellingPlan) {
          formData.append(`items[${mainIndex}][selling_plan]`, mainSellingPlan);
          formData.append(`items[${mainIndex}][properties][_subscription_id]`, mainSellingPlan);
          formData.append(`items[${mainIndex}][properties][_subscription_unique_id]`, Date.now());
        }

        if (this.isSubscriptionWithTripod) {
          formData.append(`items[${mainIndex}][properties][_tripod_subscription_type]`, "main");
        }

          this.appendUltimateEditionProperties(formData, mainIndex, ultimateEditionData);

        if (gwpInput || (shouldUseStandardGiftFromMetafield && standardGiftData)) {
          const variantId = shouldUseStandardGiftFromMetafield
            ? standardGiftData?.variantId
            : (gwpInput ? gwpInput.dataset.variantId : null);
          if (variantId) {
            formData.append(`items[${mainIndex}][properties][_gwp-product-related]`, variantId);
          }
        }
      }

      let startUpsellIndex = mainIndex + 1;

      if (upsellInputs.length) {
        for (const [index, input] of upsellInputs.entries()) {
          const itemIndex = startUpsellIndex + index;
          const variantId = input.dataset.variantId;
          const productType = input.dataset.productType;
          const preOrder = input.dataset.preOrder !== undefined;
          const lessonsCount = input.dataset.lessonsCount;
          const sellingPlanId = input.dataset.sellingPlanId;

          formData.append(`items[${itemIndex}][id]`, variantId);
          formData.append(`items[${itemIndex}][quantity]`, "1");

          if (sellingPlanId) {
            if (isSubscriptionInCart) {
              this.removeSkeletonItem();
              revertOptimisticCartItemQuantity();
              if (window.CartOptimist) CartOptimist.revert();
              if (this.cart && typeof this.cart.close === 'function') this.cart.close();
              this.showNotificationModal(window?.cartStrings?.alreadyIncludedSubscriptionError);
              this.submitButton.setAttribute('aria-disabled', false);
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading-overlay__spinner').classList.add('hidden');
              return;
            }

            formData.append(`items[${itemIndex}][selling_plan]`, sellingPlanId);
            formData.append(`items[${itemIndex}][properties][_subscription_id]`, sellingPlanId);
            formData.append(`items[${itemIndex}][properties][_subscription_unique_id]`, Date.now());
          }

          if (productType === "Matt") {
            formData.append(`items[${itemIndex}][properties][_is_product_matt]`, "true");
          }

          if (productType === "Lesson") {
            formData.append(`items[${itemIndex}][properties][_lessons]`, "true");
            formData.append(`items[${itemIndex}][properties][_lessons_count]`, lessonsCount);
          }

          if (preOrder) {
            formData.append(`items[${itemIndex}][properties][Stock status]`, "Pre-order");
          }

          if (igItemProperty) {
            const [key, value] = Object.entries(igItemProperty)[0];
            formData.append(`items[${itemIndex}][properties][${key}]`, value);
          }
        }
      }

      const fathersDayGiftVariantId = this.getFathersDayGiftVariantId();
      const fathersDayLessonVariantId = this.getFathersDayLessonVariantId();
      const fathersDayAllGiftVariantId = this.getFathersDayAllGiftVariantId();
      let nextFdIndex = startUpsellIndex + upsellInputs.length;

      if (fathersDayLessonVariantId) {
        formData.append(`items[${nextFdIndex}][id]`, fathersDayLessonVariantId);
        formData.append(`items[${nextFdIndex}][quantity]`, '1');
        formData.append(`items[${nextFdIndex}][properties][_gwp-product]`, 'true');
        formData.append(`items[${nextFdIndex}][properties][_gwp-product-for]`, baseVariantId);
        formData.append(`items[${nextFdIndex}][properties][_fathers-day-item]`, 'lesson');
        if (fathersDayAllGiftVariantId) {
          formData.append(`items[${nextFdIndex}][properties][_fathers-day-gift-variant]`, fathersDayAllGiftVariantId);
        }
        const fdoLessonInput = document.querySelector('.js-fathers-day-lesson-input');
        const fdoLessonLabel = fdoLessonInput?.dataset.title;
        if (fdoLessonLabel) {
          formData.append(`items[${nextFdIndex}][properties][_fdo-lesson-label]`, fdoLessonLabel);
        }
        const fdoPdpFdo = document.querySelector('.fathers-day-offer:not(.fathers-day-offer--cart):not(.fathers-day-offer--optimistic)');
        const fdoGiftLabel = fdoPdpFdo?.querySelector('.fathers-day-offer__toggle-input')?.getAttribute('aria-label');
        if (fdoGiftLabel) {
          formData.append(`items[${nextFdIndex}][properties][_fdo-gift-label]`, fdoGiftLabel);
        }
        nextFdIndex++;
      }

      if (fathersDayGiftVariantId) {
        formData.append(`items[${nextFdIndex}][id]`, fathersDayGiftVariantId);
        formData.append(`items[${nextFdIndex}][quantity]`, '1');
        formData.append(`items[${nextFdIndex}][properties][_gwp-product]`, 'true');
        formData.append(`items[${nextFdIndex}][properties][_gwp-product-for]`, baseVariantId);
        formData.append(`items[${nextFdIndex}][properties][_fathers-day-item]`, 'gift');
      }

      const subscriptionsToAddCount = this.getNonPrepaidSubscriptionCountFromFormData(formData);

      if (subscriptionsToAddCount > 1) {
        this.removeSkeletonItem();
        revertOptimisticCartItemQuantity();
        if (window.CartOptimist) CartOptimist.revert();
        if (this.cart && typeof this.cart.close === 'function') this.cart.close();
        this.showNotificationModal("You can't add multiple subscriptions at once. Please select only one subscription product.");
        this.submitButton.setAttribute('aria-disabled', false);
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');

        return;
      }

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      if (this.cart && this.cart.tagName === 'CART-DRAWER') {
        try { this.showOptimisticCartUpdate(formData); } catch (e) { console.error('Optimistic update failed:', e); }
      }

      /*if (this.formType === 'add-to-cart-form' && document.querySelector('cart-drawer')) {
        this.fastCartUpdate();
      }*/

      fetch(`${routes.cart_add_url}.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.removeSkeletonItem();
              revertOptimisticCartItemQuantity();
              if (window.CartOptimist) CartOptimist.revert();
              this.handleErrorMessage(response.description);
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => { this.cart.renderContents(response) });
              }, { once: true });
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response, this.formType);
            }

            this.clearOptimisticStandardUpgradeUpsell({ restoreHiddenUpsell: false });
            this.enableRenderedUpgradeButtons();
            document.dispatchEvent(new CustomEvent("init:upsell-slider"));

            updateCartAndProductJSON();
          })
          .catch((e) => {
            this.removeSkeletonItem();
            this.clearOptimisticStandardUpgradeUpsell();
            revertOptimisticCartItemQuantity();
            if (window.CartOptimist) CartOptimist.revert();
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');

            bundleProductInput && (bundleProductInput.checked = false);

            upsellInputs.forEach(input => {
              input.checked = false;
            });
          });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    showOptimisticCartUpdate(formData) {
      this.cart.open();
      this.cart.classList.remove('is-empty');

      const drawerItems = this.cart.querySelector('cart-drawer-items');
      if (drawerItems) {
        drawerItems.classList.remove('is-empty');
        const drawerInner = this.cart.querySelector('.drawer__inner');
        if (drawerInner) drawerInner.classList.remove('is-empty');
      }

      const checkoutBtn = this.cart.querySelector('#CartDrawer-Checkout');
      if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

      const container = this.cart.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
      if (!container) return;

      const formItems = parseFormDataItems(formData);
      const productData = this.getOptimisticProductData();
      const productFormItem = getFormItem(productData, formItems);
      const existingCartItem = getExistingCartItem(productFormItem);
      let optimisticItem;
      const addonContainer = this.form.closest('.additional-product__item');
      const isAddon = !!addonContainer;
      let gwpData = null;
      let fdoDataList = [];
      if (isAddon) {
        if (existingCartItem) {
          updateOptimisticCartItemQuantity(existingCartItem, productFormItem.quantity);
        } else {
          optimisticItem = this.createOptimisticCartItem(productData);
          optimisticItem && container.prepend(optimisticItem);
        }

        const cartHandles = window._cartState ? window._cartState.items.map(i => i.handle) : [];
        const variantInput = addonContainer.querySelector('form [name="id"]');
        const variantId = variantInput?.value;
        const addonConfig = typeof getAddonConfig === 'function' ? getAddonConfig() : null;
        let addedHandle = '';
        if (addonConfig) {
          const match = addonConfig.products.find(a => String(a.variant_id) === String(variantId));
          if (match) addedHandle = match.handle;
        }
        const occupiedHandles = [...cartHandles, addedHandle].filter(Boolean);
        const nextAddon = typeof getNextAvailableAddon === 'function' ? getNextAvailableAddon(occupiedHandles) : null;

        const addonSection = addonContainer.closest('.additional-product');
        if (nextAddon && addonSection) {
          const btnText = addonConfig?.button_text;
          const newItemHTML = typeof buildAddonItemHTML === 'function' ? buildAddonItemHTML(nextAddon, btnText) : '';
          if (newItemHTML) {
            const list = addonSection.querySelector('.additional-product__list');
            if (list) {
              addonContainer.style.transition = 'opacity 0.15s ease';
              addonContainer.style.opacity = '0';
              setTimeout(() => {
                list.innerHTML = newItemHTML;
                requestAnimationFrame(() => {
                  const newItem = list.querySelector('.additional-product__item');
                  if (newItem) {
                    newItem.style.opacity = '0';
                    newItem.style.transition = 'opacity 0.15s ease';
                    requestAnimationFrame(() => { newItem.style.opacity = '1'; });
                  }
                });
              }, 150);
            }
          }
        } else if (addonSection) {
          addonSection.style.transition = 'opacity 0.15s ease, height 0.3s ease 0.1s, padding 0.3s ease 0.1s, margin 0.3s ease 0.1s';
          addonSection.style.overflow = 'hidden';
          const h = addonSection.offsetHeight;
          addonSection.style.height = h + 'px';
          requestAnimationFrame(() => {
            addonSection.style.opacity = '0';
            addonSection.style.height = '0';
            addonSection.style.paddingTop = '0';
            addonSection.style.paddingBottom = '0';
            addonSection.style.marginTop = '0';
            addonSection.style.marginBottom = '0';
          });
        }
      } else {
        const gwpDataList = this.getGWPDataForOptimistic();
        if (existingCartItem) {
          updateOptimisticCartItemQuantity(existingCartItem, productFormItem.quantity);
        } else {
          optimisticItem = this.createOptimisticCartItem(productData, !!gwpDataList?.length);
          optimisticItem && container.prepend(optimisticItem);
        }

        const ultimateEditionData = this.getUltimateEditionData();
        const shouldMarkGwpAsUltimate = ultimateEditionData.hasMultipleGifts;
        if (gwpDataList.length) {
          let anchor = optimisticItem;
          gwpDataList.forEach((gwpData) => {
            const gwpFormItem = getFormItem(gwpData, formItems);
            const existingGwpCartItem = getExistingCartItem(gwpFormItem);

            if (existingGwpCartItem) {
              updateOptimisticCartItemQuantity(existingGwpCartItem, gwpFormItem.quantity);
              return;
            }

            if (!anchor) {
              return;
            }

            const gwpPill = this.createOptimisticGWP(gwpData, { isUltimate: shouldMarkGwpAsUltimate });
            if (!gwpPill) return;
            anchor.after(gwpPill);
            anchor = gwpPill;
          });
        }
        gwpData = gwpDataList;

        fdoDataList = this.getFDODataForOptimistic();
        if (fdoDataList.length) {
          if (!existingCartItem) {
            const fdoEl = this.createOptimisticFDO();
            if (fdoEl) {
              let fdoAnchor = optimisticItem;
              let next = fdoAnchor?.nextElementSibling;
              while (next && next.classList.contains('cart-item--gwp')) {
                fdoAnchor = next;
                next = fdoAnchor.nextElementSibling;
              }
              fdoAnchor ? fdoAnchor.after(fdoEl) : container.prepend(fdoEl);
            }
          } else {
            const pdpFdo = document.querySelector('.fathers-day-offer:not(.fathers-day-offer--cart):not(.fathers-day-offer--optimistic)');
            const pdpToggle = pdpFdo?.querySelector('.fathers-day-offer__toggle-input');
            if (pdpToggle?.checked) {
              const cartFdoToggle = this.cart?.querySelector('.fathers-day-offer--cart .js-fdo-cart-toggle');
              if (cartFdoToggle && !cartFdoToggle.checked) {
                cartFdoToggle.checked = true;
                cartFdoToggle.nextElementSibling
                  ?.querySelector('.fathers-day-offer__icon-wrap')
                  ?.classList.remove('fathers-day-offer__icon-wrap--inactive');
              }
            }
          }
        }

        this.renderOptimisticStandardUpgradeUpsell();

        if (this.isSubscriptionWithTripod) {
            const tripodData = this.getTripodDataForOptimistic();
            const tripodFormItem = getFormItem(tripodData, formItems);
            const existingTripodCartItem = getExistingCartItem(tripodFormItem);

            if (existingTripodCartItem) {
              updateOptimisticCartItemQuantity(existingTripodCartItem, tripodFormItem.quantity);
            } else if (optimisticItem) {
              const tripodItem = this.createOptimisticCartItem(tripodData);
              optimisticItem.after(tripodItem);
            }
        }
      }

      if (productData && productData.price && window.CartOptimist) {
        const gwpCount = Array.isArray(gwpData) ? gwpData.length : 0;
        const fdoCount = Array.isArray(fdoDataList) ? fdoDataList.length : 0;
        CartOptimist.optimisticAdd(productData.price, productData.compareAtPrice || 0, gwpCount + fdoCount);
      }
    }

    getOptimisticProductData() {
      const addonContainer = this.form.closest('.additional-product__item');
      if (addonContainer) {
        const img = addonContainer.querySelector('.additional-product__image img');
        const nameEl = addonContainer.querySelector('.additional-product__name');
        const priceWrapper = addonContainer.querySelector('.additional-product__price');
        const linkEl = addonContainer.querySelector('.additional-product__image a');
        const isOnSale = priceWrapper?.querySelector('.price--on-sale');

        let price = 0;
        let compareAtPrice = 0;
        if (isOnSale) {
          const salePriceEl = priceWrapper.querySelector('.price__sale .price-item--sale');
          const comparePriceEl = priceWrapper.querySelector('.price__sale s.price-item--regular');
          price = this.parsePriceFromDOM(salePriceEl);
          compareAtPrice = this.parsePriceFromDOM(comparePriceEl);
        } else {
          const regularPriceEl = priceWrapper?.querySelector('.price__regular .price-item--regular');
          price = this.parsePriceFromDOM(regularPriceEl);
        }

        let sellingPlanName = '';
        const priceSourceEl = isOnSale
          ? priceWrapper.querySelector('.price__sale .price-item--sale')
          : priceWrapper?.querySelector('.price__regular .price-item--regular');
        if (priceSourceEl) {
          sellingPlanName = this.extractSellingPlanName(priceSourceEl.textContent);
        }
        const hasSellingPlan = !!addonContainer.querySelector('form [name="selling_plan"]')?.value;
        const variantId = addonContainer.querySelector('form [name="id"]')?.value;

        return {
          title: nameEl?.textContent?.trim() || 'Product',
          image: img?.src || '',
          price: price,
          compareAtPrice: compareAtPrice,
          url: linkEl?.href || '#',
          sellingPlanName: sellingPlanName,
          isSubscription: hasSellingPlan || !!sellingPlanName,
          variantId: variantId || ''
        };
      }

      if (this.currentProductObjectJSON) {
        const product = this.currentProductObjectJSON;
        const sellingPlanId = this.form.querySelector('[name="selling_plan"]')?.value;
        const hasSellingPlan = !!sellingPlanId;
        const sellingPlanName = hasSellingPlan
          ? this.getSellingPlanShortNameById(sellingPlanId)
            || this.extractSellingPlanNameFromPriceBlock(this.mainProductSection || this.closest('.product') || this)
          : '';

        const widget = this.getUltimateEditionWidget();
        const selectedWidgetVariantId = widget
          ?.querySelector('[data-ultimate-edition-option][aria-pressed="true"]')
          ?.dataset?.variantId
          || widget
            ?.querySelector('[data-ultimate-edition-option].is-active')
            ?.dataset?.variantId
          || null;
        const currentVariant = this.getCurrentVariantData(selectedWidgetVariantId);
        const widgetPriceData = this.getPriceDataFromUltimateWidget();
        const ultimateEditionData = this.getUltimateEditionData();
        let ultimateEditionIconHtml = '';
        if (ultimateEditionData.isUltimateSelected && widget) {
          ultimateEditionIconHtml = widget.querySelector('.ultimate-edition-widget__upgrade-icon')?.innerHTML?.trim() || '';
        }

        const variantImage = currentVariant?.featured_image?.src
          || currentVariant?.featured_image?.url
          || '';

        const variantId = currentVariant?.id || this.form.querySelector('[name="id"]')?.value;

        return {
          title: product.title,
          image: variantImage || product.media?.[0]?.preview_image?.src || product.featured_image || '',
          price: widgetPriceData?.price || currentVariant?.price || product.price,
          compareAtPrice: widgetPriceData?.compareAtPrice || currentVariant?.compare_at_price || product.compare_at_price || 0,
          url: this.currentProductUrl || '#',
          sellingPlanName: sellingPlanName,
          isSubscription: hasSellingPlan,
          ultimateEditionLabel: ultimateEditionData.isUltimateSelected ? ultimateEditionData.title : '',
          ultimateEditionIconHtml,
          variantId: variantId || ''
        };
      }

      const pf = this.closest('product-form') || this;
      const variantId = pf.querySelector('form [name="id"]')?.value;
      if (pf.dataset.upsellPrice) {
        return {
          title: pf.dataset.upsellTitle || 'Product',
          image: pf.dataset.upsellImage || '',
          price: parseInt(pf.dataset.upsellPrice, 10) || 0,
          compareAtPrice: parseInt(pf.dataset.upsellCompareAtPrice, 10) || 0,
          url: pf.dataset.upsellUrl || '#',
          sellingPlanName: pf.dataset.upsellSellingPlanName || '',
          isSubscription: !!(pf.dataset.upsellSellingPlanName || '').trim(),
          variantId: variantId || ''
        };
      }

      return null;
    }

    parsePriceFromDOM(el) {
      if (!el) return 0;
      const raw = String(el.textContent || '').replace(/\s/g, '').replace(/[^0-9,.-]/g, '');
      if (!raw) return 0;

      let cleaned = raw.replace(/-/g, '');
      const comma = cleaned.lastIndexOf(',');
      const dot = cleaned.lastIndexOf('.');
      let decimalSep = null;

      if (comma !== -1 && dot !== -1) {
        decimalSep = comma > dot ? ',' : '.';
      } else if (comma !== -1) {
        const commaDigits = cleaned.length - comma - 1;
        if (commaDigits > 0 && commaDigits <= 2) decimalSep = ',';
      } else if (dot !== -1) {
        const dotDigits = cleaned.length - dot - 1;
        if (dotDigits > 0 && dotDigits <= 2) decimalSep = '.';
      }

      if (decimalSep) {
        const thousandSep = decimalSep === ',' ? '.' : ',';
        cleaned = cleaned.replace(new RegExp(`\\${thousandSep}`, 'g'), '').replace(decimalSep, '.');
      } else {
        cleaned = cleaned.replace(/[.,]/g, '');
      }

      const parsed = parseFloat(cleaned);
      return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
    }

    getPriceDataFromUltimateWidget() {
      const widget = this.getUltimateEditionWidget();
      if (!widget) return null;

      const selectedOption = widget.querySelector('[data-ultimate-edition-option][aria-pressed="true"]')
        || widget.querySelector('[data-ultimate-edition-option].is-active');
      if (!selectedOption) return null;

      const priceRoot = selectedOption.querySelector('.ultimate-edition-widget__price-wrap');
      if (!priceRoot) return null;

      const salePriceEl = priceRoot.querySelector('.price__sale .price-item--sale');
      const comparePriceEl = priceRoot.querySelector('.price__sale .price-item--regular');
      const regularPriceEl = priceRoot.querySelector('.price__regular .price-item--regular')
        || priceRoot.querySelector('.price-item--regular');

      if (salePriceEl) {
        return {
          price: this.parsePriceFromDOM(salePriceEl),
          compareAtPrice: this.parsePriceFromDOM(comparePriceEl),
        };
      }

      if (regularPriceEl) {
        return {
          price: this.parsePriceFromDOM(regularPriceEl),
          compareAtPrice: 0,
        };
      }

      return null;
    }

    extractSellingPlanName(text) {
      if (!text) return '';
      const normalizedText = String(text).trim();
      const slashIdx = normalizedText.indexOf('/');
      if (slashIdx === -1) return '';
      return normalizedText.substring(slashIdx + 1).trim();
    }

    extractSellingPlanNameFromPriceBlock(root) {
      if (!root) return '';

      const candidates = [
        '.price .price__sale .price-item--sale',
        '.price .price__regular .price-item--regular',
      ];

      for (const selector of candidates) {
        const el = root.querySelector(selector);
        const value = this.extractSellingPlanName(el?.textContent);
        if (value) return value;
      }

      return '';
    }

    getSellingPlanShortNameById(sellingPlanId) {
      if (!sellingPlanId || !Array.isArray(this.currentProductSellingPlansJSON)) return '';

      const matchedPlan = this.currentProductSellingPlansJSON.find((plan) => {
        return String(plan?.id) === String(sellingPlanId);
      });

      if (!matchedPlan) return '';

      return String(matchedPlan.short_name || matchedPlan.shortName || matchedPlan.name || '').trim();
    }

    createOptimisticCartItem(productData, related = false) {
      const item = document.createElement('div');
      item.setAttribute('role', 'row');
      const isCartRedesign = document.querySelector('#CartDrawer.cart-drawer--redesign');

      if (!productData) {
        item.className = 'cart-item cart-item--skeleton';
        item.innerHTML = `
          <div class="cart-item__media skeleton-pulse"></div>
          <div class="cart-item__content-wrapper">
            <div class="skeleton-content">
              <div class="skeleton-line skeleton-pulse" style="width:60%;height:14px"></div>
              <div class="skeleton-line skeleton-pulse" style="width:80%;height:20px;margin-top:10px"></div>
              <div class="skeleton-line skeleton-pulse" style="width:100px;height:34px;margin-top:12px;border-radius:60px"></div>
            </div>
          </div>`;
        return item;
      }

      const priceFormatted = typeof formatMoneyWithoutTrailingZeros === 'function'
        ? formatMoneyWithoutTrailingZeros(productData.price)
        : formatMoney(productData.price, '${{amount}}');
      const planSuffix = productData.sellingPlanName ? ` / ${productData.sellingPlanName}` : '';
      const imgSrc = productData.image
        ? productData.image.split('?')[0] + '?width=200&height=200&crop=center'
        : '';

      let priceHTML = '';
      if (productData.compareAtPrice && productData.compareAtPrice > productData.price) {
        const compareFormatted = typeof formatMoneyWithoutTrailingZeros === 'function'
          ? formatMoneyWithoutTrailingZeros(productData.compareAtPrice)
          : formatMoney(productData.compareAtPrice, '${{amount}}');
        const compareAtTemplate = window?.cartStrings?.compareAtPriceValue || '[price]';
        const compareAtText = compareAtTemplate.replace('[price]', compareFormatted);
        const savingsSuffix = window?.cartStrings?.savingsBadgeSuffix || 'OFF';
        const savingsPercent = Math.round(((productData.compareAtPrice - productData.price) / productData.compareAtPrice) * 100);
        priceHTML = `
          <div class="cart-item__discounted-prices">
            <strong class="cart-item__final-price cart-item__price">${priceFormatted}${planSuffix}</strong>
            <s class="cart-item__old-price cart-item__price">${compareAtText}</s>
            ${!isCartRedesign ? `<span class="cart-item__final-price-badge badge price__badge-sale">${savingsPercent}% ${savingsSuffix}</span>` : ''}
          </div>`;
      } else {
        priceHTML = `<div class="cart-item__price">${priceFormatted}${planSuffix}</div>`;
      }

        const ultimateEditionLabelHtml = productData.ultimateEditionLabel
            ? `<div class="cart-item__ultimate-edition">
                <span class="cart-item__ultimate-edition-icon" aria-hidden="true">
                  ${productData.ultimateEditionIconHtml || ''}
                </span>
                <span class="cart-item__ultimate-edition-text">${productData.ultimateEditionLabel.toUpperCase()}</span>
              </div>`
            : '';

      const quantityControlsHTML = productData.isSubscription || productData.isTripod
        ? `<input class="js-cart-item-quantity" type="hidden" name="updates[]" value="1" />`
        : `
            <quantity-input class="quantity">
              <button class="quantity__button no-js-hidden" name="minus" type="button">
                <span class="visually-hidden">Decrease quantity</span>
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
                </svg>
              </button>
              <input class="quantity__input" type="number" value="1" min="0" aria-label="Quantity" />
              <button class="quantity__button no-js-hidden" name="plus" type="button">
                <span class="visually-hidden">Increase quantity</span>
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
                </svg>
              </button>
            </quantity-input>
          `;

      const removeButtonHTML = `
        <cart-remove-button>
          <button class="button button--tertiary" type="button" aria-label="Remove ${productData.title}">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14 3.98665C11.78 3.76665 9.54667 3.65332 7.32 3.65332C6 3.65332 4.68 3.71999 3.36 3.85332L2 3.98665" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5.66675 3.31325L5.81341 2.43992C5.92008 1.80659 6.00008 1.33325 7.12675 1.33325H8.87341C10.0001 1.33325 10.0867 1.83325 10.1867 2.44659L10.3334 3.31325" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.5667 6.09326L12.1334 12.8066C12.06 13.8533 12 14.6666 10.14 14.6666H5.86002C4.00002 14.6666 3.94002 13.8533 3.86668 12.8066L3.43335 6.09326" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6.88672 11H9.10672" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6.33325 8.33325H9.66659" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </cart-remove-button>
      `;

      item.className = `cart-item cart-item--optimistic${related ? ' cart-item--gwp-related' : ''}`;
      item.innerHTML = `
        <div class="cart-item__media">
          ${imgSrc ? `<img class="cart-item__image" src="${imgSrc}" alt="${productData.title}" width="100" height="100" style="object-fit:cover" />` : ''}
          <a class="cart-item__link" href="${productData.url}" aria-label="${productData.title}"></a>
        </div>
        <div class="cart-item__content-wrapper">
          <div class="cart-item__details">
            <a class="cart-item__name cart-item__name--link h4 break" href="${productData.url}">${productData.title}</a>
            ${ultimateEditionLabelHtml}
            ${isCartRedesign ? removeButtonHTML : ''}
          </div>
          <div class="cart-item__quantity">
            ${!isCartRedesign ? priceHTML : ''}
            <div class="cart-item__quantity-wrapper">
              ${quantityControlsHTML}
              ${isCartRedesign ? priceHTML : ''}
            </div>
          </div>
        </div>
        ${!isCartRedesign ? removeButtonHTML : ''}`;
      return item;
    }

    getGWPDataForOptimistic() {
      const ultimateEditionData = this.getUltimateEditionData();
      const widget = this.mainProductSection
        ? this.mainProductSection.querySelector('[data-ultimate-edition]')
        : null;
      const standardGiftData = this.getStandardGiftData();
      const gwpInput = this.getGiftProductInput();

      if (ultimateEditionData.isUltimateSelected && widget) {
        const stateBlock = widget.querySelector(
          `[data-ultimate-edition-state][data-variant-id="${widget.dataset.ultimateVariantId}"]`
        );

        if (stateBlock) {
          const giftItems = Array.from(stateBlock.querySelectorAll('.ultimate-edition-widget__gift-item'));
          const gifts = giftItems.map((giftItem) => {
            const title = giftItem.querySelector('.ultimate-edition-widget__gift-title')?.textContent?.trim() || '';
            const subtitle = giftItem.querySelector('.ultimate-edition-widget__gift-description')?.textContent?.trim() || '';
            const image = giftItem.querySelector('.ultimate-edition-widget__gift-media img')?.src || '';
            const variantId = giftItem.dataset.variantId;

            return {
              title: title || 'Gift',
              image,
              subtitle,
              variantId: variantId || ''
            };
          }).filter((gift) => gift.title || gift.subtitle || gift.image);

          if (gifts.length) return gifts;
        }
      }

      if (ultimateEditionData.isStandardSelected) {
        if (widget) {
          const giftsSource = widget.querySelector('[data-standard-gifts-source]');
          const giftItems = Array.from(giftsSource?.querySelectorAll('.ultimate-edition-widget__gift-item') || []);
          const gifts = giftItems.map((giftItem) => ({
            title: giftItem.querySelector('.ultimate-edition-widget__gift-title')?.textContent?.trim() || '',
            image: giftItem.querySelector('.ultimate-edition-widget__gift-media img')?.src || '',
            subtitle: giftItem.querySelector('.ultimate-edition-widget__gift-description')?.textContent?.trim() || '',
            variantId: giftItem.dataset.variantId || ''
          })).filter((gift) => gift.title || gift.subtitle || gift.image);

          if (gifts.length) return gifts;
        }

        const fallbackCache = window._gwpCache || {};
        const variantId = standardGiftData?.variantId || gwpInput?.dataset?.variantId;
        if (!variantId) return [];

        return [{
          title: standardGiftData?.displayTitle || standardGiftData?.title || gwpInput?.dataset?.displayTitle || gwpInput?.dataset?.title || fallbackCache.title || '',
          image: standardGiftData?.image || gwpInput?.dataset?.image || fallbackCache.image || '',
          subtitle: standardGiftData?.subtitle || gwpInput?.dataset?.subtitle || fallbackCache.subtitle || '',
          variantId: variantId || ''
        }];
      }

      if (!gwpInput) {
        return [];
      }

      if (window._cartState) {
        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const bundleInput = this.mainProductSection && this.mainProductSection.querySelector('.js-bundle-product-input:checked');
        const baseVariantId = bundleInput ? bundleInput.dataset.variantId : mainVariantId;
      }

      if (gwpInput.dataset.variantId) {
        return [{
          title: gwpInput.dataset.displayTitle || gwpInput.dataset.title || standardGiftData?.displayTitle || standardGiftData?.title || '',
          image: gwpInput.dataset.image || standardGiftData?.image || '',
          subtitle: gwpInput.dataset.subtitle || standardGiftData?.subtitle || '',
          variantId: gwpInput.dataset.variantId || '',
        }];
      }

      if (window._gwpCache) return [window._gwpCache];

      return [];
    }

    getFDODataForOptimistic() {
      const result = [];
      const lessonInput = document.querySelector('.js-fathers-day-lesson-input');
      if (lessonInput?.dataset.variantId) {
        result.push({
          title: lessonInput.dataset.title || 'Free Lesson',
          image: '',
          subtitle: '',
          variantId: lessonInput.dataset.variantId,
        });
      }
      const pdpFdo = document.querySelector('.fathers-day-offer:not(.fathers-day-offer--cart):not(.fathers-day-offer--optimistic)');
      const giftToggle = pdpFdo?.querySelector('.fathers-day-offer__toggle-input');
      if (giftToggle?.checked && giftToggle.dataset.giftVariantId) {
        result.push({
          title: giftToggle.getAttribute('aria-label') || 'Free Gift Card',
          image: '',
          subtitle: '',
          variantId: giftToggle.dataset.giftVariantId,
        });
      }
      return result;
    }

    createOptimisticFDO() {
      const src = document.querySelector('.fathers-day-offer:not(.fathers-day-offer--cart)');
      if (!src) return null;

      const clone = src.cloneNode(true);
      clone.classList.add('fathers-day-offer--cart', 'fathers-day-offer--optimistic');

      clone.querySelector('.js-fathers-day-lesson-input')?.remove();

      const srcToggle = src.querySelector('.fathers-day-offer__toggle-input');
      const cloneToggle = clone.querySelector('.fathers-day-offer__toggle-input');
      if (srcToggle && cloneToggle) {
        cloneToggle.checked = srcToggle.checked;
        cloneToggle.disabled = true;
      }

      return clone;
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getOptimisticStandardUpgradeUpsellData() {
      const ultimateEditionData = this.getUltimateEditionData();
      if (!ultimateEditionData.isStandardSelected || !ultimateEditionData.standardUpgradeToVariantId) return null;

      const widget = this.getUltimateEditionWidget();
      if (!widget) return null;

      const standardOption = widget.querySelector('[data-ultimate-edition-option][aria-pressed="true"]')
        || widget.querySelector('[data-ultimate-edition-option].is-active');
      const ultimateVariantId = widget.dataset.ultimateVariantId;
      const ultimateOption = widget.querySelector(`[data-ultimate-edition-option][data-variant-id="${ultimateVariantId}"]`);
      if (!standardOption || !ultimateOption) return null;

      const standardPriceData = this.getPriceDataFromUltimateWidget();
      const ultimateSalePriceEl = ultimateOption.querySelector('.ultimate-edition-widget__price-wrap .price__sale .price-item--sale');
      const ultimateRegularPriceEl = ultimateOption.querySelector('.ultimate-edition-widget__price-wrap .price__regular .price-item--regular')
        || ultimateOption.querySelector('.ultimate-edition-widget__price-wrap .price-item--regular');
      const ultimatePrice = this.parsePriceFromDOM(ultimateSalePriceEl || ultimateRegularPriceEl);
      const amountCents = Math.max(ultimatePrice - (standardPriceData?.price || 0), 0);
      const moneyFormat = typeof getMoneyNoDecimalsFormat === 'function' ? getMoneyNoDecimalsFormat() : '${{amount_no_decimals}}';
      const amount = formatMoney(amountCents, moneyFormat);
      const buttonTemplate = window?.cartStrings?.ultimateUpgradeButtonTemplate || 'Upgrade to Ultimate for [amount]';
      const buttonLabel = buttonTemplate.replace('[amount]', amount);

      const ultimateState = widget.querySelector(`[data-ultimate-edition-state][data-variant-id="${ultimateVariantId}"]`);
      const savingsText = ultimateState?.querySelector('.ultimate-edition-widget__upgrade-save')?.textContent?.trim() || '';
      const iconHtml = widget.querySelector('.ultimate-edition-widget__upgrade-icon')?.innerHTML?.trim() || '';

      const gifts = ultimateState
        ? Array.from(ultimateState.querySelectorAll('.ultimate-edition-widget__gift-item')).map((giftItem) => ({
            title: giftItem.querySelector('.ultimate-edition-widget__gift-title')?.textContent?.trim() || '',
            description: giftItem.querySelector('.ultimate-edition-widget__gift-description')?.textContent?.trim() || '',
            image: giftItem.querySelector('.ultimate-edition-widget__gift-media img')?.getAttribute('src') || '',
            price: giftItem.querySelector('.ultimate-edition-widget__gift-price')?.textContent?.trim() || '',
          })).filter((gift) => gift.title || gift.description || gift.image || gift.price)
        : [];

      return {
        title: ultimateEditionData.standardUpgradeToTitle || ultimateOption.querySelector('.ultimate-edition-widget__title')?.textContent?.trim() || 'Ultimate Edition',
        image: ultimateEditionData.standardUpgradeToImage || '',
        savingsText,
        iconHtml,
        gifts,
        buttonLabel,
        variantId: ultimateVariantId || '',
      };
    }

    renderOptimisticStandardUpgradeUpsell() {
      const data = this.getOptimisticStandardUpgradeUpsellData();
      const drawerContent = this.cart?.querySelector('.drawer__content');
      if (!drawerContent) return;

      this.clearOptimisticStandardUpgradeUpsell();
      if (!data) return;

      const oldAdditional = drawerContent.querySelector(':scope > .additional-product:not(.additional-product--optimistic-ultimate-upgrade)');
      if (oldAdditional) {
        oldAdditional.style.display = 'none';
        oldAdditional.setAttribute('data-optimistic-hidden-upsell', 'true');
      }

      const giftsHtml = data.gifts.map((gift) => `
        <li class="ultimate-edition-widget__gift-item">
          <div class="ultimate-edition-widget__gift-media">
            ${gift.image ? `<img src="${this.escapeHtml(gift.image)}" alt="${this.escapeHtml(gift.title || '')}" width="20" height="20" loading="lazy" />` : ''}
          </div>
          <div class="ultimate-edition-widget__gift-content">
            <span class="ultimate-edition-widget__gift-title">${this.escapeHtml(gift.title)}</span>
          </div>
          ${gift.price ? `<s class="ultimate-edition-widget__gift-price">${this.escapeHtml(gift.price)}</s>` : ''}
        </li>
      `).join('');

      const optimisticUpsell = document.createElement('div');
      optimisticUpsell.className = 'additional-product additional-product--ultimate-upgrade additional-product--optimistic-ultimate-upgrade';
      optimisticUpsell.innerHTML = `
        <div class="ultimate-edition-widget__upgrade">
          <div class="ultimate-edition-widget__upgrade-top">
            <span class="ultimate-edition-widget__upgrade-title">
              <span class="ultimate-edition-widget__upgrade-icon" aria-hidden="true">${data.iconHtml || ''}</span>
              ${this.escapeHtml(String(data.title || '').toUpperCase())}
            </span>
            ${data.savingsText ? `<span class="ultimate-edition-widget__upgrade-save">${this.escapeHtml(data.savingsText)}</span>` : ''}
          </div>
          <div class="ultimate-edition-widget__gifts-wrap">
            ${data.image ? `<div class="ultimate-edition-widget__gifts-background" aria-hidden="true"><img src="${this.escapeHtml(data.image)}" alt="" width="424" height="220" loading="lazy" /></div>` : ''}
            <ul class="ultimate-edition-widget__gifts">${giftsHtml}</ul>
            <div class="additional-product__ultimate-upgrade-cta">
              <button class="additional-product__button cart-item__ultimate-upgrade-button is-optimistic-disabled" type="button" disabled aria-disabled="true">
                <span class="cart-item__ultimate-upgrade-button-hover">
                  <span class="cart-item__ultimate-upgrade-button-text">${this.escapeHtml(data.buttonLabel)}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      `;

      const cartDrawerItems = drawerContent.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.insertAdjacentElement('afterend', optimisticUpsell);
      } else {
        drawerContent.appendChild(optimisticUpsell);
      }
    }

      getTripodDataForOptimistic() {
          const variantId = this.isSubscriptionWithTripod.value;
          const { title, image, url, price, compareAtPrice } = this.isSubscriptionWithTripod.dataset;

          if (!variantId || !title || !image || !url || !price || !compareAtPrice) {
              return null;
          }

          return {
            title,
            image,
            url,
            price: Number(price),
            compareAtPrice: Number(compareAtPrice),
            isTripod: true,
            variantId: variantId || ''
          };
      }

    clearOptimisticStandardUpgradeUpsell(options = {}) {
      const restoreHiddenUpsell = options.restoreHiddenUpsell !== false;
      const drawerContent = this.cart?.querySelector('.drawer__content');
      if (!drawerContent) return;

      drawerContent.querySelectorAll('.additional-product--optimistic-ultimate-upgrade').forEach((el) => el.remove());
      if (restoreHiddenUpsell) {
        drawerContent.querySelectorAll('[data-optimistic-hidden-upsell="true"]').forEach((el) => {
          el.style.display = '';
          el.removeAttribute('data-optimistic-hidden-upsell');
        });
      }
    }

    enableRenderedUpgradeButtons() {
      const drawer = this.cart;
      if (!drawer) return;

      drawer.querySelectorAll('.additional-product--ultimate-upgrade .cart-item__ultimate-upgrade-button[disabled]').forEach((button) => {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-optimistic-disabled');
      });
    }

    createOptimisticGWP(data, options = {}) {
      if (!data || (!data.title && !data.image && !data.subtitle)) {
        return null;
      }

      const pill = document.createElement('div');
      const ultimateClass = options.isUltimate ? ' cart-item--ultimate' : '';
      pill.className = `cart-item cart-item--gwp cart-item--optimistic-gwp${ultimateClass}`;
      pill.setAttribute('role', 'row');
      const imgHTML = data.image
        ? `<img class="cart-item--gwp__image" src="${data.image}" alt="${data.title}" width="24" height="24" />`
        : '';
      pill.innerHTML = `
        <div class="cart-item--gwp__inner">
          ${imgHTML}
          <span class="cart-item--gwp__text">
            <strong>${data.title}</strong>
            <span class="cart-item--gwp__subtitle">${data.subtitle}</span>
          </span>
          <span class="cart-item--gwp__badge">Included</span>
        </div>`;
      return pill;
    }

    removeSkeletonItem() {
      this.cart?.querySelectorAll('.cart-item--skeleton, .cart-item--optimistic, .cart-item--optimistic-gwp, .fathers-day-offer--optimistic')
        .forEach(el => el.remove());
    }

    /*fastCartUpdate() {
      this.cart.open();

      if (!this.productElJSON) return null;

      this.isCurrentProductInCart = this.currentCartObjectJSON?.items.some(item => item['variant_id'] === Number(this.currentAddedProductVariantId));
      this.cartItemsContainer = this.cart.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');

      if (!this.cartItemsContainer) return null;

      if (this.isCurrentProductInCart) {
        this.currentProductInCartIndexPosition = this.currentCartObjectJSON?.items.findIndex(item => item['variant_id'] === Number(this.currentAddedProductVariantId));
        this.currentProductInCartObjectJSON = this.currentCartObjectJSON?.items.find(item => item['variant_id'] === Number(this.currentAddedProductVariantId));
        this.cartItemThatNeedUpdate = this.cartItemsContainer.querySelector(`#CartDrawer-Item-${this.currentProductInCartIndexPosition + 1}`);

        if (!this.cartItemThatNeedUpdate) return null;

        this.cartItemThatNeedUpdateQuantity = this.cartItemThatNeedUpdate.querySelector('.js-cart-item-quantity');
        this.cartItemThatNeedUpdateQuantity.value = this.currentProductInCartObjectJSON['quantity'] + 1;
      } else if (!this.isCurrentProductInCart && this.currentCartObjectJSON['item_count'] !== 0) {
        this.newCartItem = this.createCartItemMarkup(
            this.currentProductObjectJSON,
            this.currentProductUrl,
            Shopify.formatMoney(this.currentProductObjectJSON.price, window.money_with_currency_format).split(" ")[0]
        );
        this.cartItemsContainer.prepend(this.newCartItem);
      } else if (this.currentCartObjectJSON['item_count'] === 0) {
        this.cart.classList.contains('is-empty') && this.cart.classList.remove('is-empty');
        this.cartDrawerItems = this.cart.querySelector('cart-drawer-items');

        if (!this.cartDrawerItems) return null;

        this.cartDrawerItems.classList.contains('is-empty') && this.cartDrawerItems.classList.remove('is-empty');
        this.newCartItem = this.createCartItemMarkup(
            this.currentProductObjectJSON,
            this.currentProductUrl,
            Shopify.formatMoney(this.currentProductObjectJSON.price, window.money_with_currency_format).split(" ")[0]
        );
        this.cartItemsContainer.prepend(this.newCartItem);
      }

      this.updateSubtotalValue();
    }

    createCartItemMarkup(productJSON, productUrl, productPrice) {
      const item = document.createElement('div');
      item.classList.add('cart-item');
      item.setAttribute('id', `CartDrawer-Item-1`);
      item.setAttribute('role', `row`);
      item.innerHTML = `
        <div class="cart-item__media" role="cell">
          <picture>
            <source 
              src="${productJSON.featured_image}&amp;crop=center&amp;height=80&amp;width=80" 
              srcset="${productJSON.featured_image}&amp;crop=center&amp;height=80&amp;width=80 1x, ${productJSON.featured_image}&amp;crop=center&amp;height=160&amp;width=160 2x" 
              media="(max-width: 480px)"
            />

            <img 
              class="cart-item__image" 
              src="${productJSON.featured_image}&amp;crop=center&amp;height=100&amp;width=100" 
              srcset="${productJSON.featured_image}&amp;crop=center&amp;height=100&amp;width=100 1x, ${productJSON.featured_image}&amp;crop=center&amp;height=200&amp;width=200 2x" 
              alt="${productJSON.title}" 
              loading="lazy" 
              width="100" 
              height="200"
            />
          </picture>
          
          <a class="cart-item__link" href="${productUrl}" aria-label="${productJSON.title}"></a>
        </div>

        <div class="cart-item__content-wrapper">
          <div class="cart-item__details" role="cell">
            <a class="cart-item__name cart-item__name--link h4 break" href="${productUrl}">${productJSON.title}</a>
          </div>

          <div class="cart-item__quantity" role="cell">
            <div>
              <div class="cart-item__price">${productPrice}</div>
            </div>

            <div class="cart-item__quantity-wrapper">
              <quantity-input class="quantity">
                <button class="quantity__button no-js-hidden" name="minus" type="button">
                  <span class="visually-hidden">Decrease quantity for ${productJSON.title}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
                  </svg>
                </button>

                <input class="quantity__input" type="number" name="updates[]" value="1" min="0" aria-label="Quantity for ${productJSON.title}" id="Drawer-quantity-1" data-index="1">

                <button class="quantity__button no-js-hidden" name="plus" type="button">
                  <span class="visually-hidden">Increase quantity for ${productJSON.title}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
                  </svg>
                </button>
              </quantity-input>
            </div>

            <div class="cart-item__error" id="CartDrawer-LineItemError-1" role="alert">
              <small class="cart-item__error-text"></small>

              <svg class="icon icon-error" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 13 13">
                <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"></circle>
                <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"></circle>
                <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"></path>
                <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7"></path>
              </svg>
            </div>
          </div>
        </div>

        <cart-remove-button id="CartDrawer-Remove-1" data-index="1">
          <button class="button button--tertiary" type="button" aria-label="Remove ${productJSON.title}">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14 3.98665C11.78 3.76665 9.54667 3.65332 7.32 3.65332C6 3.65332 4.68 3.71999 3.36 3.85332L2 3.98665" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M5.66675 3.31325L5.81341 2.43992C5.92008 1.80659 6.00008 1.33325 7.12675 1.33325H8.87341C10.0001 1.33325 10.0867 1.83325 10.1867 2.44659L10.3334 3.31325" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M12.5667 6.09326L12.1334 12.8066C12.06 13.8533 12 14.6666 10.14 14.6666H5.86002C4.00002 14.6666 3.94002 13.8533 3.86668 12.8066L3.43335 6.09326" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M6.88672 11H9.10672" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M6.33325 8.33325H9.66659" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </cart-remove-button>                         
      `;

      return item;
    }

    updateSubtotalValue() {
      this.cartDrawerSubtotal = this.cart.querySelector('.js-cart-drawer-subtotal');

      if (!this.cartDrawerSubtotal) return null;

      this.newCartDrawerSubtotal = this.currentCartObjectJSON.total_price + this.currentProductObjectJSON.price;
      this.cartDrawerSubtotal.textContent = Shopify.formatMoney(this.newCartDrawerSubtotal, window.money_with_currency_format);
    }*/
  });
}

(() => {
  const selector = '[data-discount-feedback]';
  const appliedClass = 'is-discount-applied';
  const confirmingClass = 'is-discount-confirming';
  const confirmDuration = 1200;
  const appliedPrefix = '\u2713 ';

  const showAppliedState = (badge) => {
    if (!badge) return;

    if (!badge.dataset.discountOriginalText) {
      badge.dataset.discountOriginalText = badge.textContent.trim().replace(/^\u2713\s*/, '');
    }

    window.clearTimeout(Number(badge.dataset.discountFeedbackTimer || 0));

    badge.textContent = badge.dataset.discountFeedbackLabel || 'Discount applied';
    badge.classList.add(appliedClass, confirmingClass);

    const timer = window.setTimeout(() => {
      badge.textContent = `${appliedPrefix}${badge.dataset.discountOriginalText || ''}`;
      badge.classList.remove(confirmingClass);
      delete badge.dataset.discountFeedbackTimer;
    }, confirmDuration);

    badge.dataset.discountFeedbackTimer = String(timer);
  };

  document.addEventListener('click', (event) => {
    const badge = event.target.closest(selector);
    if (!badge) return;

    showAppliedState(badge);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const badge = event.target.closest(selector);
    if (!badge) return;

    event.preventDefault();
    showAppliedState(badge);
  });
})();
